import React, { FunctionComponent, useEffect } from "react";
import { Typography, Link, Box, Tooltip } from "@mui/material";
import moment from "moment";
import ReactToPrint from "react-to-print";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router";
import Barcode from "react-barcode";
import "./PrintBill.scss";
import classNames from "classnames";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { tl, t } from "../../../components/translate";
import { registerPrint } from "../../../actions/bill";
import {
  DocumentTypes,
  BillStatus,
  BillType,
  PrintTemplate
} from "../../../interfaces/BillInterfaces";
import { dateWithZeroBeforeSingleDigits } from "../../../helpers/formatters";
import { isOldFormatBill } from "../../../actions/helpers/billHelper";
import DescribeBillV1 from "./BillDescriptionV1";
import DescribeBillV2Half from "./BillDescriptionV2Half";
import usePrintStyles from "../../../hooks/usePrintStyles";
import { getSectionStyle, scaleFont } from "../../Lab/LabPrint/LabPrintFunctions";
import LabPrintLetterHead from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import { BillClientInfoSmall, checkWalkInCustomer, InfoField } from "./BillPrintHelpers";
import LabPrintLetterHeadCentered from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterheadCentered";
import logo from "../../../../assets/images/poweredByOkhati.png";
import { RootState } from "../../../store";
import { User } from "../../../interfaces/User";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import usePrintShortcut from "../../../hooks/usePrintShortcut";
import { getPrintTitle } from "./PrintBill";
import QrFromUrl from "../../../components/Qr/QrFromUrl";
import { DataComment } from "../../Lab/LabPrint/Templates/DefaultTemplate/DefaultTemplateCore";
import { hasValidRichTextRemark } from "../Editor/BilllEditorActions";
import CustomHeader from "../../Lab/LabPrint/CustomHeader";
import { rupeeDisplay } from "../../../helpers/rupee";

interface PrintBillProps {
  showDobOnPrint?: boolean;
  hidePaymentMethodOnPrint?: boolean;
  buttonText?: string;
  billId: number;
  resourceCentre: ResourceCentre;
  billData: BillType;
  customerNumber: string;
  sendPrintInfo: (id) => void;
  user: User;
  SPs: ServiceProvider;
  forceTemplate?: string;
  labelStyle?: React.CSSProperties;
  clientBalance: number;
  addPrintCount?: boolean;
  onPrintClick?: () => void;
  isForPreview?: boolean;
  labTestUrl?: string;
  printAsEstimate?: boolean;
}

export const disclaimerText =
  "Disclaimer: This is just the preview of bill. This cannot be issued to customer yet.";

const PrintBill: FunctionComponent<PrintBillProps> = ({
  billId,
  billData,
  buttonText = "Print",
  resourceCentre,
  sendPrintInfo,
  user,
  SPs,
  forceTemplate,
  labelStyle = {},
  clientBalance,
  addPrintCount = true,
  onPrintClick = () => null,
  showDobOnPrint = false,
  hidePaymentMethodOnPrint = false,
  isForPreview = false,
  labTestUrl,
  printAsEstimate = false
}) => {
  const history = useHistory();
  const location = useLocation<{ openPrintWindow?: boolean }>();

  // refs
  const printRef = React.useRef<HTMLDivElement>(null);
  const reactPrintRef = React.useRef<HTMLDivElement>(null);

  // state
  const [isPrinting, setIsPrinting] = React.useState(false);

  // variables
  const showDueOnBillPrint = localStorage.getItem("showDueOnBillPrint") === "true";
  const billDue = (billData.summary?.totalAmount || 0) - (billData.paymentInfo.paidAmount || 0);
  const showLetterhead =
    resourceCentre?.settings?.printSettings.billPrintSettings?.includeLetterhead;
  const showFooter = resourceCentre?.settings?.printSettings.billPrintSettings?.includeFooter;

  const clinicNameFontScale = resourceCentre?.settings?.printSettings.clinicNameFontScale;
  const centralizedHeader: boolean = resourceCentre?.settings?.printSettings.centralizedHeader;
  const showReferrer = resourceCentre?.settings?.printSettings.includeReferrer;
  const headerColor: string =
    (resourceCentre?.settings.printSettings.labPrintSettings.enableColor &&
      resourceCentre?.settings.printSettings.labPrintSettings.color) ||
    null;
  const panNo = resourceCentre?.settings?.billingSettings?.panNo;
  const customTitle = resourceCentre?.settings?.printSettings?.billPrintSettings?.customTitle;
  const customSubtitle = resourceCentre?.settings?.printSettings?.billPrintSettings?.customSubtitle;
  const customHeader = resourceCentre?.settings?.billingSettings?.customHeaderImage;
  const hideRcLogo = Boolean(customTitle);

  let letterHeadComponent = <Box />;
  if (showLetterhead) {
    if (centralizedHeader) {
      letterHeadComponent = (
        <LabPrintLetterHeadCentered
          resourceCentre={{
            ...resourceCentre,
            panNo
          }}
          titleFontScale={clinicNameFontScale}
          hideRcLogo={hideRcLogo}
        />
      );
    } else {
      letterHeadComponent = (
        <LabPrintLetterHead
          resourceCentre={{
            ...resourceCentre,
            panNo
          }}
          titleFontScale={clinicNameFontScale}
          hideRcLogo={hideRcLogo}
        />
      );
    }
  }

  const b4PrintStyles = usePrintStyles({
    pageStyle: `size: 5.5in 8.5in landscape; margin: 0mm;margin-bottom: 12pt;
          counter-increment: page;
            @bottom-center {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 8pt;
          }`
  });

  const defaultPrintStyles = usePrintStyles({
    pageStyle: `size: A5; margin: 0mm;margin-bottom: 12pt;
          counter-increment: page;
            @bottom-center {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 8pt;
          }`
  });

  // functions
  const applyCustomPrintStyle = React.useCallback(() => {
    if (forceTemplate === PrintTemplate.B4) {
      b4PrintStyles.applyPrintStyles();
    } else {
      defaultPrintStyles.applyPrintStyles();
    }
  }, [defaultPrintStyles, forceTemplate, b4PrintStyles]);

  // effects
  useEffect(() => {
    const triggerPrint = async () => {
      if (
        location.state?.openPrintWindow &&
        (!billData?.document?.createdLabTestIds?.length ||
          (billData?.document?.createdLabTestIds?.length && Boolean(labTestUrl)))
      ) {
        applyCustomPrintStyle();
        reactPrintRef?.current?.click();
        history.replace({ ...location, state: { ...location.state, openPrintWindow: false } });
      }
    };

    triggerPrint();
  }, [
    labTestUrl,
    billData?.document?.createdLabTestIds?.length,
    location.state,
    location,
    history,
    applyCustomPrintStyle
  ]);

  usePrintShortcut(reactPrintRef, applyCustomPrintStyle);

  if (!billData || !billData.document) {
    return null;
  }

  return (
    <>
      <ReactToPrint
        onAfterPrint={() => {
          window.focus();
          if (addPrintCount) {
            sendPrintInfo(billId);
          }
          // eslint-disable-next-line react-hooks/rules-of-hooks
          usePrintStyles({
            pageStyle: "size: A4; margin: 0mm"
          }).applyPrintStyles();
          setIsPrinting(false);
        }}
        onBeforePrint={() => {
          if (onPrintClick) onPrintClick();
        }}
        trigger={() => (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link
            className="actionPrintBtn"
            onMouseEnter={applyCustomPrintStyle}
            onMouseLeave={() => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              usePrintStyles({
                pageStyle: "size: A4; margin: 0mm"
              }).applyPrintStyles();
            }}
            style={{ textDecoration: "none", padding: "5px 15px" }}
          >
            <Tooltip title="Alt + P / Ctrl + P" arrow>
              <Typography component="span" style={labelStyle}>
                <Box
                  ref={reactPrintRef}
                  onClick={() => {
                    if (isPrinting) return;
                    setIsPrinting(true);
                  }}
                  className="actionBtnLabel"
                  component="span"
                >
                  {buttonText || "Print"}
                </Box>
              </Typography>
            </Tooltip>
          </Link>
        )}
        content={() => printRef.current}
      />
      <iframe title="halfBillPrintContent" className="displayContents">
        <div ref={printRef}>
          <Box
            component="table"
            width="100%"
            className={classNames({ "water-marks": billData.isDraft })}
            waterMarkText={printAsEstimate || !isForPreview ? "" : disclaimerText}
          >
            <tfoot>
              <tr>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <div className="billFooterOffset" />
                </td>
              </tr>
            </tfoot>

            <thead>
              <tr>
                <td>
                  {customHeader ? (
                    <CustomHeader imageUrl={customHeader} />
                  ) : (
                    <Box
                      style={{
                        ...getSectionStyle(headerColor).headerStyle,
                        paddingTop: "0.2cm",
                        marginBottom: "0"
                      }}
                    >
                      {letterHeadComponent}
                    </Box>
                  )}
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Box
                    style={{
                      ...getSectionStyle(headerColor).bodyStyle,
                      marginTop: "0",
                      paddingTop: "0"
                    }}
                  >
                    <Box
                      component="div"
                      display="flex"
                      marginBottom="0.8cm"
                      width="100%"
                      justifyContent="center"
                    >
                      <Typography style={{ textAlign: "center" }}>
                        <Box
                          fontSize="0.45cm"
                          marginTop="0.5cm"
                          marginBottom="0.3cm"
                          fontWeight={400}
                        >
                          <Typography component="span" sx={{ textTransform: "uppercase" }}>
                            {billData.type === DocumentTypes.CREDITNOTE && tl("billing.creditNote")}{" "}
                          </Typography>
                          {billData.type !== DocumentTypes.CREDITNOTE && (
                            <Box sx={{ height: "0.25cm" }}>
                              <Typography sx={{ textTransform: "uppercase" }}>
                                {`${getPrintTitle({
                                  billStatus: billData.status,
                                  billType: billData.type,
                                  printAsEstimate,
                                  customTitle
                                })}  ${
                                  billData.status === BillStatus.cancelled
                                    ? t("billing.cancelled")
                                    : (!isForPreview &&
                                        Boolean(billData?.printCount) &&
                                        ` (COPY of Original ${billData?.printCount})`) ||
                                      ""
                                }`}
                              </Typography>
                              {!printAsEstimate && (
                                <Typography sx={{ textTransform: "uppercase", fontSize: "0.28cm" }}>
                                  {customSubtitle}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Typography>
                    </Box>
                    <Box
                      component="div"
                      display="flex"
                      justifyContent="space-between"
                      borderTop="1px solid #bbb"
                      p="0.1cm"
                    >
                      <Box component="div" width="50%">
                        {checkWalkInCustomer(billData.client, "isWalkInCustomer") && (
                          <BillClientInfoSmall
                            client={billData.client}
                            customerNumber={billData.customer?.customerNumber}
                            showDobOnPrint={showDobOnPrint}
                          />
                        )}
                        <Barcode
                          value={billId}
                          width={2}
                          displayValue={false}
                          fontSize={5}
                          height={20}
                          margin={2}
                          font="arial"
                        />
                      </Box>

                      <Box component="div" width="40%">
                        {billData.ipd && (
                          <InfoField
                            label={<>IPD No.</>}
                            data={<>{billData.ipd.ipdNumber || ""}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData.document.bed?.name && (
                          <InfoField
                            label={<>Bed No.</>}
                            data={<>{billData.document.bed.name}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {!printAsEstimate && (
                          <InfoField
                            label={
                              <>
                                {billData.type === DocumentTypes.CREDITNOTE
                                  ? tl("billing.creditNoteNumber")
                                  : tl("billnumber")}
                              </>
                            }
                            data={<>{billData.billNumber}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData.type === DocumentTypes.CREDITNOTE && (
                          <InfoField
                            label={<>{tl("billing.creditNoteForBill")}</>}
                            data={<>{billData.related?.billNumber}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        <InfoField
                          label={<> {tl("billing.issueDate")}</>}
                          data={
                            <>
                              {dateWithZeroBeforeSingleDigits(
                                convertADtoBS(moment(billData.issueDate)).formatted
                              )}{" "}
                              {moment(billData.issueDate).format("h:mm a")}
                            </>
                          }
                          labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                          valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                        />
                        <InfoField
                          label={<>{tl("billing.dateAD")}</>}
                          data={
                            <>
                              {dateWithZeroBeforeSingleDigits(
                                moment(billData.issueDate).format("YYYY/MM/DD")
                              )}{" "}
                            </>
                          }
                          labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                          valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                        />
                        {billData.document.referredBy && (
                          <InfoField
                            label={<>{tl("booking.referredBy")}</>}
                            data={<>{billData.document.referredBy.toUpperCase()}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData.document.extraReferrer && (
                          <InfoField
                            label="Extra Referrer"
                            data={<>{billData.document.extraReferrer.name.toUpperCase()}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData.ipd?.doctorInCharge && (
                          <InfoField
                            label={<>Consulted by</>}
                            data={
                              <>
                                {`${billData.ipd.doctorInCharge?.firstName || ""} ${
                                  billData.ipd.doctorInCharge?.lastName || ""
                                }`}
                              </>
                            }
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData.insuranceNumber && (
                          <InfoField
                            label={tl("billing.insuranceNumber")}
                            data={<>{billData.insuranceNumber} </>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData.claimNumber && (
                          <InfoField
                            label={tl("billing.claimNumber")}
                            data={<>{billData.claimNumber} </>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}
                        {billData?.document?.ssf?.id && (
                          <InfoField
                            label={tl("billing.ssid")}
                            data={<>{billData.document.ssf.id}</>}
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}

                        {billData?.document?.ssf?.id && billData?.document?.ssf?.scheme?.value && (
                          <InfoField
                            label={tl("billing.scheme")}
                            data={
                              <>
                                {`${billData.document.ssf.scheme.label} (${billData.document.ssf.scheme.value})`}
                              </>
                            }
                            labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                            valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                          />
                        )}

                        {billData?.document?.ssf?.id &&
                          billData?.document?.ssf?.subProduct?.value && (
                            <InfoField
                              label={tl("billing.subScheme")}
                              data={
                                <>
                                  {`${billData.document.ssf.subProduct.label} (${billData.document.ssf.subProduct.value})`}
                                </>
                              }
                              labelStyle={{ width: "2cm", fontSize: scaleFont("0.33cm", 0.85) }}
                              valueStyle={{ fontSize: scaleFont("0.33cm", 0.85) }}
                            />
                          )}
                      </Box>
                    </Box>

                    {/* check the version and load bill content accordingly,
                    may use switch case if third version comes */}
                    {isOldFormatBill(billData.document.version) ? (
                      <DescribeBillV1 billData={billData} SPs={SPs} />
                    ) : (
                      <DescribeBillV2Half
                        hidePaymentMethodOnPrint={hidePaymentMethodOnPrint}
                        clientBalance={clientBalance}
                        billData={billData}
                        SPs={SPs}
                        relatedSettings={{
                          showReferrer,
                          isBordered: false,
                          headerColor
                        }}
                        user={user}
                      />
                    )}
                  </Box>
                </td>
              </tr>
            </tbody>
          </Box>
          <div className="billFooterPos" style={{ width: "100%" }}>
            {/* previous due amount */}
            {showDueOnBillPrint && clientBalance < 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "5px",
                  padding: "0 10mm",
                  gap: "5px"
                }}
              >
                <Typography fontSize={scaleFont("0.25cm")}>
                  {tl("billing.previousTotalDue")}:
                </Typography>
                <Typography fontSize={scaleFont("0.25cm")}>
                  {rupeeDisplay(Math.abs(clientBalance))}
                </Typography>
              </Box>
            )}

            {/* more visible remark */}
            {hasValidRichTextRemark(billData?.document?.remarks) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "10px",
                  padding: "5px 10mm",
                  gap: "5px"
                }}
              >
                <Typography fontSize={scaleFont("0.25cm")}>{tl("billing.remarks")}:</Typography>
                <DataComment comment={billData.document.remarks} />
              </Box>
            )}

            {showFooter && (
              <Box
                style={{ ...getSectionStyle(headerColor).footerStyle }}
                sx={{
                  width: "100%",
                  paddingTop: "0.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                {labTestUrl && billDue <= 0 && (
                  <QrFromUrl url={labTestUrl} label={t("billPrint.labTestQrLabel")} />
                )}
                <img src={logo} alt="logo" style={{ height: "35px", marginLeft: "auto" }} />
              </Box>
            )}
          </div>
        </div>
      </iframe>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { resourceCentres } = state.resources;
  const rcId = state.userContext.resourceCentreId;

  const getResourceCentre = (id, item) => item.find((rc) => rc.id === id);

  return {
    SPs: state.resources.resourceCentreServiceProviders,
    user: state.userContext.user,
    resourceCentre: getResourceCentre(rcId, resourceCentres) || state.userContext.resourceCentre
  };
};

const mapDispatchToProps = (dispatch) => ({
  sendPrintInfo: (id) => dispatch(registerPrint(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(PrintBill);
